/**
 * **Ist das erkennbar eine Firma?** — abgelesen am Namen, nicht gefragt.
 *
 * Lisa, Systemprüfung 23.09.2026 (A-03): *„Die Maske verlangt eine USt-IdNr.
 * als Pflichtfeld. Bei einem Privatkunden gibt es die in aller Regel nicht …
 * nur verlangen, wenn es sich erkennbar um ein Unternehmen handelt."*
 *
 * Der Betrieb kann USt-IdNr. und Steuernummer beim Kunden zur Pflicht machen
 * (Einstellungen · Pflichtfelder). Eine Regel, die für **jeden** Kunden gilt,
 * hat dafür aber keinen Grund (Regel 11): Lexware Office braucht keine
 * USt-IdNr., um einen Kontakt anzulegen, und ein Privatmann, der einen
 * Rüttler mietet, hat keine. Die Pflicht greift darum nur, **wenn der Name
 * eine Rechtsform trägt** — GmbH, KG, e.K., UG, AG …
 *
 * **Abgeleitet statt gefragt** (Regel 11): Ein Schalter „Privatkunde" wäre ein
 * Feld mehr am Telefon und eine Spalte mehr im Datenmodell. Die Rechtsform
 * steht ohnehin im Namen, weil sie auf der Rechnung steht. Wer keine trägt —
 * ein Privatmann, ein Einzelunternehmer ohne Eintrag —, wird nicht nach einer
 * Steuerkennung gefragt, die er meistens nicht hat; eintragen kann er sie
 * trotzdem.
 *
 * **Nachtrag 24.09.2026 — der Kundentyp ist jetzt ein Feld** (Systemprüfung
 * L2/W2, Entscheidung (B)). Lisa: *„Ein Auswahlfeld wäre eindeutiger"* — wann
 * die Erkennung am Namen greift, war für sie nicht nachvollziehbar. Luca hat
 * es so entschieden: `customer.customer_kind` (`'privat'` | `'gewerblich'`,
 * Migration 0187), gewählt beim Anlegen, und die Pflichtfelder sind je Typ
 * einstellbar. **Die Erkennung bleibt — als Rückfall.** Ein Bestandskunde
 * ohne Typ wird nicht zum Nachpflegen gezwungen; für ihn gilt, was der Name
 * sagt, genau wie bis heute (`wirksamerKundentyp`).
 */
/**
 * Die Rechtsformen, an denen eine Firma zu erkennen ist — Deutschland,
 * Österreich, Schweiz, Niederlande und die üblichen englischen. Punkte und
 * Leerzeichen innerhalb der Abkürzung sind freigestellt („e.K.", „e. K.",
 * „eK"), groß oder klein spielt keine Rolle.
 */
const RECHTSFORMEN = [
    'g?gmbh', 'mbh', 'gesmbh', 'ug', 'ag', 'kg', 'kgaa', 'ohg', 'gbr', 'partg', 'partgmbb',
    'e\\.?\\s?k(?:fm|fr)?\\.?', 'e\\.?\\s?v\\.?', 'eg', 'se',
    'b\\.?\\s?v\\.?', 'n\\.?\\s?v\\.?', 'sarl', 's\\.?\\s?a\\.?', 'sagl',
    'ltd\\.?', 'limited', 'inc\\.?', 'llc', 'plc',
];
/** Vor und hinter der Rechtsform steht ein Trenner oder der Rand des Namens. */
const MUSTER = new RegExp(`(?:^|[\\s,(&/+-])(?:${RECHTSFORMEN.join('|')})(?=$|[\\s,)&/+-])`, 'i');
/** `true`, wenn der Name eine Rechtsform trägt. Leer ist keine Firma. */
export function erkennbarFirma(name) {
    if (!name)
        return false;
    return MUSTER.test(name.trim());
}
/**
 * Die Felder, die nur bei einem gewerblichen Kunden Pflicht sein können —
 * die Feldnamen von `POST /kunden`.
 */
export const NUR_BEI_FIRMA = ['ust_id', 'steuernummer'];
/** Die zwei Kundentypen (Migration 0187). Deutsch, weil es Fachbegriffe sind. */
export const KUNDENTYPEN = ['privat', 'gewerblich'];
/** Ist das ein Kundentyp? Alles andere — leer, Tippfehler — heißt „nicht gesetzt". */
export function istKundentyp(wert) {
    return typeof wert === 'string' && KUNDENTYPEN.includes(wert);
}
/**
 * **Der Typ, nach dem gerechnet wird** — der gesetzte, sonst der abgelesene.
 *
 * Gesetzt schlägt abgelesen: Wer „privat" wählt, meint es, auch wenn der Name
 * „… GbR" trägt. Nicht gesetzt (Bestand vor 0187, ein Kontakt aus Lexware)
 * gilt, was bis heute galt — eine Rechtsform im Namen heißt gewerblich.
 */
export function wirksamerKundentyp(typ, name) {
    if (istKundentyp(typ))
        return typ;
    return erkennbarFirma(name) ? 'gewerblich' : 'privat';
}
/** Gilt dieses Pflichtfeld für diesen Kundentyp? Alle übrigen gelten immer. */
export function pflichtGiltFuer(feld, typ) {
    return NUR_BEI_FIRMA.includes(feld) ? typ === 'gewerblich' : true;
}
/**
 * Die Spalten, für die der Betrieb Pflichtfelder einstellt (Migration 0098,
 * seit 0187 zwei für den Kunden). Interessent und Vermieter bleiben je eine:
 * Dem einen schreiben wir noch keine Rechnung, dem anderen nie.
 */
export const PFLICHTROLLEN = ['kunde_privat', 'kunde_gewerblich', 'interessent', 'vermieter'];
/** Welche Pflichtfeldliste gilt — aus der Rolle und dem wirksamen Typ. */
export function pflichtrolleFuer(art, typ) {
    if (art === 'kunde')
        return typ === 'gewerblich' ? 'kunde_gewerblich' : 'kunde_privat';
    return art;
}
/* ══════════════════════════════════════════════════════════════════
   Was einem Kunden für die Rechnung fehlt — eine Regel für Akte und Start.

   Bis zum 24.09.2026 lebte diese Regel nur im Browser (`Luecken.tsx`), und
   nur wer die Akte aufschlug, sah sie. Lisa (W2): *„wird es dort nicht
   eingetragen, muss das vorne sichtbar sein … sonst kann am Ende keine
   Rechnung raus, ohne dass das jemandem auffällt."* Die Startseite fragt
   dieselbe Regel im Server („Zu prüfen"), die Akte im Browser — darum steht
   sie hier und nicht zweimal.

   **Das Zahlungsziel ist keine Lücke** (Entscheidung W2 (A), 24.09.2026):
   Es steht immer da, und 14 Tage sind der gewollte Normalfall. Eine Spalte,
   die nie leer sein kann, als „fehlt" zu melden, hieße, ein Feld zu pflegen,
   das schon stimmt (Regel 11).
   ══════════════════════════════════════════════════════════════════ */
/** Was fehlen kann — in der Reihenfolge der Akte. */
export const STAMMDATENLUECKEN = ['anschrift', 'telefon', 'ust_id', 'rechnung_email', 'ansprechpartner'];
const leer = (wert) => !wert || wert.trim() === '';
/**
 * Die Lücken eines Kunden, gemessen an den Pflichtfeldern seines Typs.
 *
 * **Immer:** die Anschrift — auf eine Rechnung gehört eine — und die
 * Rechnungs-E-Mail. **Beim gewerblichen Kunden** USt-IdNr. oder
 * Steuernummer. **Was der Betrieb zur Pflicht gemacht hat:** Telefon,
 * Zusatz, Ansprechpartner.
 *
 * Eine Aufzählung, keine Sperre: Lexware Office legt den Kontakt auch ohne
 * Rechnungs-E-Mail an, und der Kunde bleibt Kunde.
 */
export function stammdatenLuecken(k, pflicht) {
    const p = new Set(pflicht);
    const a = k.anschrift ?? {};
    const typ = wirksamerKundentyp(k.typ, k.name);
    const luecken = [
        leer(a.strasse) || leer(a.plz) || leer(a.ort) || (p.has('zusatz') && leer(a.zusatz)) ? 'anschrift' : null,
        p.has('telefon') && leer(k.telefon) ? 'telefon' : null,
        typ === 'gewerblich' && leer(k.ust_id) && leer(k.steuernummer) ? 'ust_id' : null,
        leer(k.rechnung_email) ? 'rechnung_email' : null,
        (p.has('ansprechpartner') || p.has('ansprechpartner_telefon') || p.has('ansprechpartner_email'))
            && !k.ansprechpartner ? 'ansprechpartner' : null,
    ];
    return luecken.filter((x) => x !== null);
}
//# sourceMappingURL=kunde.js.map